import { zodResolver } from '@hookform/resolvers/zod';
import type { CountryCode } from '@noconflict/contracts';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BillingCurrencyNotice } from '../../../entities/org';
import { Button } from '../../../shared/ui/button';
import { CountrySelect } from '../../../shared/ui/country-select';
import { Field } from '../../../shared/ui/field';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../shared/ui/form';
import { Input } from '../../../shared/ui/input';
import { type SignUpInput, type SignUpSubmit, type SignUpValues, signUpSchema } from './model';

export function SignUpForm({
  onSubmit,
  defaultCountry,
}: {
  onSubmit: SignUpSubmit;
  defaultCountry?: CountryCode;
}) {
  const { t } = useTranslation('auth');
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<SignUpInput, unknown, SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      ...(defaultCountry ? { country: defaultCountry } : {}),
    },
  });
  const { register, handleSubmit, formState } = form;
  // The geo lookup may answer after the first render: prefill then, unless a country was already chosen.
  useEffect(() => {
    if (defaultCountry && !form.getValues('country')) form.setValue('country', defaultCountry);
  }, [defaultCountry, form]);
  return (
    <Form {...form}>
      <form
        className="space-y-4"
        onSubmit={handleSubmit(async (values) => {
          setServerError((await onSubmit(values)).error);
        })}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('fields.firstName')} error={formState.errors.firstName?.message}>
            <Input id="first-name" autoComplete="given-name" {...register('firstName')} />
          </Field>
          <Field label={t('fields.lastName')} error={formState.errors.lastName?.message}>
            <Input id="last-name" autoComplete="family-name" {...register('lastName')} />
          </Field>
        </div>
        <Field label={t('fields.email')} error={formState.errors.email?.message}>
          <Input id="email" type="email" autoComplete="email" {...register('email')} />
        </Field>
        <Field label={t('fields.password')} error={formState.errors.password?.message}>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            {...register('password')}
          />
        </Field>
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.country')}</FormLabel>
              <FormControl>
                <CountrySelect value={field.value} onValueChange={field.onChange} />
              </FormControl>
              <FormDescription>
                <BillingCurrencyNotice country={field.value} />
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        {serverError ? (
          <p role="alert" className="text-sm text-destructive">
            {serverError}
          </p>
        ) : null}
        <Button type="submit" className="w-full" disabled={formState.isSubmitting}>
          {t('signUp.submit')}
        </Button>
      </form>
    </Form>
  );
}
